import {
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import { FileDownloadOutlined } from '@mui/icons-material';
import { dialogOverlay, StyledDialogActions, SuccessActionButton } from '../styles';
import { GenericModal } from '@/components/shared/GenericModal';
import { idmsTemplates } from '../data';
import InfoIcon from '@mui/icons-material/Info';
import { IdmsUploadsTypeEnum } from '@/__generated__/graphql';
import ExcelJS from 'exceljs';

type Props = {
  open: boolean;
  handleClose: () => void;
  title?: string;
  description?: string;
};

export const TemplatesModal = ({ open, handleClose, title, description }: Props) => {
  const convertCsvToXlsx = async (csvText: string, templateName: string): Promise<Blob> => {
    // Parse CSV content
    const lines = csvText.trim().split('\n');
    const headers = lines[0].split(',');
    
    // Create data rows
    const dataRows: string[][] = [];
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        dataRows.push(lines[i].split(','));
      }
    }
    
    // Create workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(templateName);
    
    // Add headers
    worksheet.columns = headers.map(header => ({
      header,
      key: header,
      width: 20
    }));
    
    // Add data rows
    dataRows.forEach(row => {
      const rowData: Record<string, string> = {};
      headers.forEach((header, index) => {
        rowData[header] = index < row.length ? row[index] : '';
      });
      worksheet.addRow(rowData);
    });
    
    // Generate Excel file as blob
    const buffer = await workbook.xlsx.writeBuffer();
    return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  };
  
  const downloadTemplate = async (path: string, name: string) => {
    try {
      // Fetch the CSV template
      const response = await fetch(path);
      const csvText = await response.text();
      
      // Convert CSV to XLSX
      const xlsxBlob = await convertCsvToXlsx(csvText, name);
      
      // Generate filename with xlsx extension
      const baseName = path.split('/').pop() ?? 'template';
      const xlsxFileName = baseName.replace('.csv', '.xlsx');
      
      // Download the file
      const url = URL.createObjectURL(xlsxBlob);
      const link = Object.assign(document.createElement('a'), { 
        href: url, 
        download: xlsxFileName 
      });
      document.body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
    }
  };
  
  const downloadAllTemplates = async () => {
    for (const template of idmsTemplates) {
      await downloadTemplate(template.path, template.name);
    }
  };

  const handleDownloadAll = async () => {
    await downloadAllTemplates();
    handleClose();
  };

  return (
    <GenericModal
      open={open}
      handleClose={handleClose}
      title={title}
      description={description}
      crossIconStyle={{ backgroundColor: 'transparent', color: 'primary' }}
      sx={{ ...dialogOverlay, left: '37% !important', width: 536, minHeight: 572 }}
    >
      <List sx={{ maxHeight: 392, overflowY: 'auto' }}>
        {idmsTemplates.map(({ name, type, path }) => (
          <ListItem
            data-testid="file-type-list-item"
            disableGutters
            key={type}
            sx={{ py: 0, px: 2, borderTop: 1, borderColor: 'divider' }}
          >
            <ListItemButton disableRipple sx={{ '&:hover': { background: 'none' } }}>
              <ListItemAvatar>
                <img
                  src="/assets/icons/file-icon.svg"
                  alt="File_Preview"
                  width={30}
                  height={40}
                />
              </ListItemAvatar>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.4rem', fontWeight: '600' }}
                primary={name}
              />
            </ListItemButton>
            <ListItemIcon sx={{ minWidth: 40, alignItems: 'center' }}>
              {type === IdmsUploadsTypeEnum.UpdateEquipments && (
                <Tooltip
                  title={`This template is used to update equipments. 
                    First column will be the line number and you can add new columns with their names in the file. 
                    This data will be inserted against that line and a filter will also be created for that data`}
                  arrow
                  placement="top"
                >
                  <InfoIcon sx={{ cursor: 'pointer' }} />
                </Tooltip>
              )}
              <IconButton onClick={() => downloadTemplate(path, name)}>
                <FileDownloadOutlined />
              </IconButton>
            </ListItemIcon>
          </ListItem>
        ))}
      </List>

      <StyledDialogActions>
        <Button onClick={handleClose} variant="text" sx={{ mr: 1, height: '36px' }}>
          Cancel
        </Button>
        <SuccessActionButton
          variant="contained"
          startIcon={<FileDownloadOutlined />}
          onClick={handleDownloadAll}
        >
          Download All
        </SuccessActionButton>
      </StyledDialogActions>
    </GenericModal>
  );
};
